import React, { useState } from "react";
import { useSubscription } from "../contexts/SubscriptionContext";
import { createPortalSession } from "../api/subscriptionClient";
import { formatDateLong } from "../utils/formatters";

export default function SubscriptionSettings() {
    const { subscription, loading } = useSubscription();
    const [portalLoading, setPortalLoading] = useState(false);
    const [error, setError] = useState("");

    const handleManageSubscription = async () => {
        setError("");
        setPortalLoading(true);

        try {
            const { portalUrl } = await createPortalSession();
            window.location.href = portalUrl;
        } catch (err) {
            setError(err.response?.data?.message || "Failed to open billing portal");
        } finally {
            setPortalLoading(false);
        }
    };


    const getStatusBadge = (status) => {
        const statusConfig = {
            active: { bg: "bg-accent-50", text: "text-accent-600", label: "Active" },
            trialing: { bg: "bg-accent-50", text: "text-accent-600", label: "Trial" },
            past_due: { bg: "bg-amber-50", text: "text-amber-600", label: "Past Due" },
            canceled: { bg: "bg-red-50", text: "text-danger-500", label: "Canceled" },
            inactive: { bg: "bg-gray-100", text: "text-gray-500", label: "Inactive" },
        };
        const config = statusConfig[status] || statusConfig.inactive;
        return (
            <span className={`px-2 py-1 rounded text-xs font-medium ${config.bg} ${config.text}`}>
                {config.label}
            </span>
        );
    };

    if (loading) {
        return (
            <div className="card p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Subscription</h2>
                <div className="animate-pulse space-y-3">
                    <div className="h-4 bg-gray-100 rounded w-1/3"></div>
                    <div className="h-4 bg-gray-100 rounded w-1/2"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Subscription</h2>

            {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-danger-500">{error}</p>
                </div>
            )}

            <div className="space-y-4">
                {/* Status */}
                <div className="flex items-center justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-500">Status</span>
                    {getStatusBadge(subscription?.status)}
                </div>

                {/* Current Period End */}
                {subscription?.currentPeriodEnd && (
                    <div className="flex items-center justify-between py-2 border-b border-gray-200">
                        <span className="text-gray-500">
                            {subscription?.status === "trialing" ? "Trial ends" : "Renews on"}
                        </span>
                        <span className="text-gray-900">
                            {formatDateLong(subscription.currentPeriodEnd)}
                        </span>
                    </div>
                )}

                {/* Trial info */}
                {subscription?.trialEndsAt && subscription?.status === "trialing" && (
                    <div className="p-3 bg-accent-50 border border-accent-200 rounded-lg">
                        <p className="text-sm text-accent-600">
                            Your trial ends on {formatDateLong(subscription.trialEndsAt)}.
                            You'll be automatically charged after the trial unless you cancel.
                        </p>
                    </div>
                )}

                {/* Past due warning */}
                {subscription?.status === "past_due" && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <p className="text-sm text-amber-600">
                            Your payment is past due. Please update your payment method to avoid losing access.
                        </p>
                    </div>
                )}

                {/* Manage Subscription Button */}
                <button
                    onClick={handleManageSubscription}
                    disabled={portalLoading}
                    className="w-full px-4 py-2 bg-gray-100 text-gray-800 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                >
                    {portalLoading ? (
                        <span className="flex items-center justify-center gap-2">
                            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            Loading...
                        </span>
                    ) : (
                        "Manage Subscription"
                    )}
                </button>

                <p className="text-xs text-gray-500 text-center">
                    Update payment method, view invoices, or cancel your subscription
                </p>
            </div>
        </div>
    );
}
